/**
 * Return Metadata Component
 *
 * Displays return metadata cards including client info, tax year, platform, and return ID.
 */

import React, { useState } from "react";
import { Button, Card, Group, Stack, Text } from "@mantine/core";
import { copyTaxYearToClipboard } from "../../api";
import type { ReturnInfo } from "../../types";

interface ReturnMetadataProps {
  returnInfo: ReturnInfo;
}

export const ReturnMetadata: React.FC<ReturnMetadataProps> = ({
  returnInfo,
}) => {
  const [copyButtonText, setCopyButtonText] = useState("Import to SmartSync");
  const [copyButtonColor, setCopyButtonColor] = useState<
    "blue" | "green" | "red"
  >("blue");

  const handleCopyTaxYear = async () => {
    try {
      await copyTaxYearToClipboard(returnInfo.year, returnInfo.clientId);
      setCopyButtonText("Copied!");
      setCopyButtonColor("green");

      setTimeout(() => {
        setCopyButtonText("Import to SmartSync");
        setCopyButtonColor("blue");
      }, 1000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      setCopyButtonText("Error!");
      setCopyButtonColor("red");

      setTimeout(() => {
        setCopyButtonText("Import to SmartSync");
        setCopyButtonColor("blue");
      }, 1000);
    }
  };

  return (
    <Group grow align="stretch" mb="xl">
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="sm">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Client
          </Text>
          <Text size="lg" fw={500}>
            {returnInfo.clientName}
          </Text>
          <Button
            component="a"
            href={`/clients/${returnInfo.clientId}/`}
            target="_blank"
            rel="noopener noreferrer"
            variant="light"
            size="sm"
          >
            View Client Profile
          </Button>
        </Stack>
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="sm">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Tax Year
          </Text>
          <Text size="lg" fw={500}>
            {returnInfo.year}
          </Text>
          <Button
            variant="light"
            size="sm"
            color={copyButtonColor}
            onClick={handleCopyTaxYear}
          >
            {copyButtonText}
          </Button>
        </Stack>
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="sm">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Platform
          </Text>
          <Text size="lg" fw={500}>
            {returnInfo.platform}
          </Text>
        </Stack>
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="sm">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Return ID
          </Text>
          <Text size="sm" fw={500} style={{ wordBreak: "break-all" }}>
            {returnInfo.id}
          </Text>
        </Stack>
      </Card>
    </Group>
  );
};
